import { useEffect, useState } from "react";
import { Download, X, Share2, PlusSquare } from "lucide-react";

const DISMISS_KEY = "sm-sop-install-dismissed-at";
const DISMISS_DAYS = 14;

function wasDismissedRecently() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function dismiss() {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    /* ignore quota errors */
  }
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * InstallPrompt — small unobtrusive bottom-right card that appears once the
 * browser fires `beforeinstallprompt` (Chrome/Edge/Samsung Internet on
 * Android, and desktop Chrome). On iOS Safari it shows manual "Add to Home
 * Screen" instructions because Apple doesn't allow programmatic install.
 *
 * Dismissed by the user → suppressed for 14 days via localStorage. Hidden
 * entirely if the app is already running in standalone mode.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (wasDismissedRecently()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);

    // iOS doesn't fire beforeinstallprompt — show a friendly hint after a short delay
    if (isIOS() && !isStandalone()) {
      const t = setTimeout(() => setVisible(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || !visible) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      setVisible(false);
      if (choice?.outcome !== "accepted") dismiss();
    } else if (isIOS()) {
      setShowIOSHelp(true);
    }
  };

  const handleDismiss = () => {
    dismiss();
    setVisible(false);
    setShowIOSHelp(false);
  };

  return (
    <>
      <div
        data-testid="install-prompt"
        style={{
          position: "fixed",
          right: 16,
          bottom: 16,
          maxWidth: 360,
          background: "var(--surface, #fff)",
          border: "1px solid var(--border, #d6dde9)",
          boxShadow: "0 12px 32px rgba(13,42,78,0.18)",
          borderRadius: 12,
          padding: 14,
          zIndex: 9999,
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        }}
      >
        <img
          src="/icons/icon-192.png"
          alt=""
          width={40}
          height={40}
          style={{ borderRadius: 8, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 700,
              color: "var(--text, #0d2a4e)",
              fontSize: 14,
            }}
          >
            Install SM Marine SOP
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--text-muted, #4a5a73)",
              marginTop: 2,
            }}
          >
            {isIOS()
              ? "Add to your home screen for a one-tap, app-like experience."
              : "Install the app for a faster, app-like experience."}
          </div>
          <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
            <button
              onClick={handleInstall}
              data-testid="install-prompt-install"
              style={{
                background: "var(--brand-blue, #1e5fbf)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 12,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                cursor: "pointer",
              }}
            >
              <Download size={12} /> {isIOS() ? "How to install" : "Install"}
            </button>
            <button
              onClick={handleDismiss}
              data-testid="install-prompt-dismiss"
              style={{
                background: "transparent",
                color: "var(--text-muted, #4a5a73)",
                border: "none",
                borderRadius: 8,
                padding: "6px 10px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Close install prompt"
          style={{
            background: "transparent",
            border: "none",
            color: "var(--text-muted, #4a5a73)",
            cursor: "pointer",
            padding: 2,
          }}
        >
          <X size={14} />
        </button>
      </div>

      {/* iOS instructions sheet */}
      {showIOSHelp && (
        <div
          onClick={() => setShowIOSHelp(false)}
          data-testid="ios-install-sheet"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(13,42,78,0.45)",
            zIndex: 10000,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 14,
              padding: 18,
              maxWidth: 420,
              width: "100%",
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              color: "#0d2a4e",
              boxShadow: "0 12px 32px rgba(13,42,78,0.25)",
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: 16,
                marginBottom: 10,
              }}
            >
              Install on iPhone / iPad
            </div>
            <ol
              style={{
                paddingLeft: 18,
                fontSize: 13,
                lineHeight: 1.6,
                color: "#4a5a73",
              }}
            >
              <li>
                Tap the <strong style={{ color: "#1e5fbf" }}>Share</strong> button{" "}
                <Share2 size={14} style={{ verticalAlign: "middle" }} /> at the
                bottom of the screen.
              </li>
              <li>
                Scroll down and tap{" "}
                <strong style={{ color: "#1e5fbf" }}>Add to Home Screen</strong>{" "}
                <PlusSquare size={14} style={{ verticalAlign: "middle" }} />.
              </li>
              <li>Tap <strong>Add</strong> in the top-right corner.</li>
            </ol>
            <button
              onClick={() => setShowIOSHelp(false)}
              style={{
                background: "var(--brand-blue, #1e5fbf)",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "8px 14px",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                width: "100%",
                marginTop: 12,
              }}
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
